import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import useUser from '../hooks/useUser';

const ProductMaster = () => {
    const toast = useRef(null);
    const navigate = useNavigate();

    const { loggedInUserId, selectedBranchId, userRole, userClient, logProcess } = useUser();

    // Dropdown options
    const [clients, setClients] = useState([]);
    const [branches, setBranches] = useState([]);
    const [categories, setCategories] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [uoms, setUoms] = useState([]);
    const [taxes, setTaxes] = useState([]);

    // Form state
    const [form, setForm] = useState({
        clientid: null,
        branchid: null,
        prcat_cat_id: null,
        category_master_id: null,
        platformid: null,
        productName: '',
        hsnCode: '',
        sortingOrder: '',
        uom: null,
        basicRate: '',
        taxPercent: '',
        taxAmount: '',
        totalPrice: '',
        discountPercent: '',
        token: '',
        tokengroup: '',
        salesItemType: null,
        productType: null,
        productImage: null,
        barcode: '',
        effectiveFrom: null,
        status: 'Active'
    });

    // Validation errors
    const [errors, setErrors] = useState({});

    const fetchClients = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/client_creation', {
                headers: {
                    'Client-Id': userClient || '',
                    'User-Role': userRole || ''
                }
            });

            const data = res.data || [];
            setClients(data);

            if (userRole && userRole !== 'GSPOS') {
                let clientToSelect = null;

                if (userClient) {
                    const found = data.find((c) => String(c.client_id) === String(userClient));
                    if (found) clientToSelect = found.client_id;
                }

                if (!clientToSelect && data.length > 0) {
                    clientToSelect = data[0].client_id;
                }

                if (clientToSelect) {
                    setForm((prev) => ({ ...prev, clientid: clientToSelect }));
                    await fetchBranches(clientToSelect);
                    await fetchCategories(clientToSelect, null);
                }
            } else {
                //GSPOS - no preselection
                setForm((prev) => ({
                    ...prev,
                    clientid: null,
                    branchid: null,
                    prcat_cat_id: null,
                    category_master_id: null
                }));
                setBranches([]);
                setCategories([]);
            }
        } catch (error) {
            console.error('Error fetching clients:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load clients'
            });
        }
    };

    const fetchBranches = async (clientId) => {
        try {
            const res = await axios.get(`https://treatway.co.in/Counter-Backend/api/server_masters/branches`, { params: { clientid: clientId } });
            setBranches(res.data.map((b) => ({ label: b.branch_name, value: b.branch_id })));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load branches' });
        }
    };

    const fetchCategories = async (clientId, branchId) => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/get_productcategory', { params: { clientid: clientId, branchid: branchId } });

            setCategories(
                res.data.map((c) => ({
                    label: c.prcat_cat_name,
                    value: {
                        prcat_cat_id: c.prcat_cat_id,
                        category_master_id: c.category_master_id
                    }
                }))
            );
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load categories' });
        }
    };

    const fetchPlatforms = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/platform');
            setPlatforms(res.data.map((p) => ({ label: p.platform_name, value: p.platform_id })));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load platforms' });
        }
    };

    const fetchUoms = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/uom');
            setUoms(res.data.map((u) => ({ label: u.uom_name, value: u.uom_name })));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load UOMs' });
        }
    };

    const fetchTaxes = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/taxpercent');
            setTaxes(res.data.map((t) => ({ label: `${t.tax_percentage}%`, value: t.tax_percentage })));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load taxes' });
        }
    };

    useEffect(() => {
        if (userRole !== undefined && userRole !== null) {
            fetchClients();
            fetchPlatforms();
            fetchUoms();
            fetchTaxes();
        }
    }, [userRole, userClient]);

    const handleChange = (key, value) => {
        let updatedForm = { ...form, [key]: value };

        if (key === 'clientid') {
            fetchBranches(value);
            setCategories([]);
            updatedForm.branchid = null;
            updatedForm.prcat_cat_id = null;
            updatedForm.category_master_id = null;
        }

        if (key === 'branchid' && updatedForm.clientid) {
            fetchCategories(updatedForm.clientid, value);
            updatedForm.prcat_cat_id = null;
            updatedForm.category_master_id = null;
        }

        if (key === 'categoryid' && value) {
            updatedForm.prcat_cat_id = value.prcat_cat_id;
            updatedForm.category_master_id = value.category_master_id;

            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.prcat_cat_id;
                return newErrors;
            });
        }

        if (key === 'taxPercent' || key === 'totalPrice') {
            const taxPercent = parseFloat(updatedForm.taxPercent) || 0;
            const totalPrice = parseFloat(updatedForm.totalPrice) || 0;

            if (totalPrice > 0) {
                if (taxPercent === 0) {
                    updatedForm.basicRate = totalPrice.toFixed(2);
                    updatedForm.taxAmount = '0.00';
                } else {
                    const basicRate = totalPrice / (1 + taxPercent / 100);
                    const taxAmount = totalPrice - basicRate;
                    updatedForm.basicRate = basicRate.toFixed(2);
                    updatedForm.taxAmount = taxAmount.toFixed(2);
                }
            } else {
                updatedForm.basicRate = '';
                updatedForm.taxAmount = '';
            }
        }

        if (key === 'effectiveFrom' && value instanceof Date) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.effectiveFrom;
                return newErrors;
            });
        }

        setForm(updatedForm);

        if (errors[key]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[key];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const requiredFields = [
            { key: 'clientid', label: 'Client' },
            { key: 'branchid', label: 'Branch' },
            { key: 'prcat_cat_id', label: 'Category' },
            { key: 'platformid', label: 'Platform' },
            { key: 'productName', label: 'Product Name' },
            { key: 'uom', label: 'UOM' },
            { key: 'basicRate', label: 'Basic Rate' },
            { key: 'taxPercent', label: 'Tax Percent' },
            { key: 'taxAmount', label: 'Tax Amount' },
            { key: 'totalPrice', label: 'Total Price' },
            { key: 'salesItemType', label: 'Sales Item Type' },
            { key: 'productType', label: 'Product Type' },
            { key: 'effectiveFrom', label: 'Effective From' },
            { key: 'status', label: 'Status' }
        ];

        let newErrors = {};

        for (let field of requiredFields) {
            const value = form[field.key];

            if (value === null || value === '' || (typeof value === 'object' && !(value instanceof Date) && Object.keys(value).length === 0)) {
                newErrors[field.key] = `${field.label} is required`;
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const confirmSave = () => {
        if (!validateForm()) return;

        confirmDialog({
            message: 'Are you sure you want to save this product?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => saveProduct()
        });
    };

    const saveProduct = async () => {
        try {
            const formData = new FormData();

            Object.entries(form).forEach(([key, value]) => {
                if (value !== null && value !== '') {
                    if (key === 'productImage' && value) {
                        formData.append('productImage', value);
                    } else if (key === 'effectiveFrom' && value instanceof Date) {
                        const formattedDate = value.toISOString().split('T')[0];
                        formData.append('effectiveFrom', formattedDate);
                    } else {
                        formData.append(key, value);
                    }
                }
            });

            const res = await axios.post('https://treatway.co.in/Counter-Backend/api/server_masters/save_product', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

            if (res.data.status === 'success') {
                toast.current.show({ severity: 'success', summary: 'Success', detail: res.data.message });
                setTimeout(() => {
                    navigate('/productlist');
                }, 1200);
                setForm({
                    clientid: null,
                    branchid: null,
                    prcat_cat_id: null,
                    category_master_id: null,
                    platformid: null,
                    productName: '',
                    hsnCode: '',
                    sortingOrder: '',
                    uom: null,
                    basicRate: '',
                    taxPercent: '',
                    taxAmount: '',
                    totalPrice: '',
                    discountPercent: '',
                    token: '',
                    tokengroup: '',
                    salesItemType: null,
                    productType: null,
                    productImage: null,
                    barcode: '',
                    effectiveFrom: null
                });
                setErrors({});
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: res.data.message });
            }
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to save product' });
        }
    };

    return (
        <div className="p-4">
            <ConfirmDialog />
            <Toast ref={toast} />
            <h2 className="text-xl font-bold mb-4">Product Master</h2>
            <div className="card">
                <div className="grid formgrid p-fluid">
                    <div className="field col-12 md:col-6">
                        <label>
                            Client <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={form.clientid}
                            options={clients.map((c) => ({ label: c.client_name, value: c.client_id }))}
                            onChange={(e) => handleChange('clientid', e.value)}
                            placeholder="Select Client"
                            className={errors.clientid ? 'p-invalid' : ''}
                            filter
                            disabled={userRole !== 'GSPOS'}
                        />
                        {errors.clientid && <small className="p-error">{errors.clientid}</small>}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>
                            Branch <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown value={form.branchid} options={branches} onChange={(e) => handleChange('branchid', e.value)} placeholder="Select Branch" filter className={errors.branchid ? 'p-invalid' : ''} />
                        {errors.branchid && <small className="p-error">{errors.branchid}</small>}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>
                            Category <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={categories.find((c) => c.value.prcat_cat_id === form.prcat_cat_id && c.value.category_master_id === form.category_master_id)?.value || null}
                            options={categories}
                            onChange={(e) => handleChange('categoryid', e.value)}
                            placeholder="Select Category"
                            filter
                            className={errors.prcat_cat_id ? 'p-invalid' : ''}
                        />
                        {errors.prcat_cat_id && <small className="p-error">{errors.prcat_cat_id}</small>}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>
                            Platform <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown value={form.platformid} options={platforms} onChange={(e) => handleChange('platformid', e.value)} placeholder="Select Platform" filter className={errors.platformid ? 'p-invalid' : ''} />
                        {errors.platformid && <small className="p-error">{errors.platformid}</small>}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>
                            Product Name <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText value={form.productName} onChange={(e) => handleChange('productName', e.target.value)} className={errors.productName ? 'p-invalid' : ''} />
                        {errors.productName && <small className="p-error">{errors.productName}</small>}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>HSN Code</label>
                        <InputText value={form.hsnCode} onChange={(e) => handleChange('hsnCode', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Sorting Order</label>
                        <InputText value={form.sortingOrder} onChange={(e) => handleChange('sortingOrder', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>
                            UOM <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown value={form.uom} options={uoms} onChange={(e) => handleChange('uom', e.value)} placeholder="Select UOM" filter className={errors.uom ? 'p-invalid' : ''} />
                        {errors.uom && <small className="p-error">{errors.uom}</small>}
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>
                            Basic Rate <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText value={form.basicRate} readOnly />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>
                            Tax Percent <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown value={form.taxPercent} options={taxes} onChange={(e) => handleChange('taxPercent', e.value)} placeholder="Select Tax %" className={errors.taxPercent ? 'p-invalid' : ''} />
                        {errors.taxPercent && <small className="p-error">{errors.taxPercent}</small>}
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>
                            Tax Amount <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText value={form.taxAmount} readOnly />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>
                            Total Price <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText value={form.totalPrice} onChange={(e) => handleChange('totalPrice', e.target.value)} className={errors.totalPrice ? 'p-invalid' : ''} />
                        {errors.totalPrice && <small className="p-error">{errors.totalPrice}</small>}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Discount %</label>
                        <InputText value={form.discountPercent} onChange={(e) => handleChange('discountPercent', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>Token</label>
                        <InputText value={form.token} onChange={(e) => handleChange('token', e.target.value)} />
                    </div>
                    <div className="field col-12 md:col-3">
                        <label>Token Group</label>
                        <InputText value={form.tokengroup} onChange={(e) => handleChange('tokengroup', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>
                            Sales Item Type <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={form.salesItemType}
                            options={[
                                { label: 'Free', value: 'free' },
                                { label: 'Paid', value: 'paid' }
                            ]}
                            onChange={(e) => handleChange('salesItemType', e.value)}
                            placeholder="Select Type"
                            className={errors.salesItemType ? 'p-invalid' : ''}
                        />
                        {errors.salesItemType && <small className="p-error">{errors.salesItemType}</small>}
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>
                            Product Type <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={form.productType}
                            options={[
                                { label: 'Veg', value: 'veg' },
                                { label: 'Non-Veg', value: 'nonveg' },
                                { label: 'Egg', value: 'egg' }
                            ]}
                            onChange={(e) => handleChange('productType', e.value)}
                            placeholder="Select Product Type"
                            className={errors.productType ? 'p-invalid' : ''}
                        />
                        {errors.productType && <small className="p-error">{errors.productType}</small>}
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>
                            Status <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={form.status}
                            options={[
                                { label: 'Active', value: 'Active' },
                                { label: 'InActive', value: 'InActive' }
                            ]}
                            onChange={(e) => handleChange('status', e.value)}
                            placeholder="Select Status"
                            className={errors.status ? 'p-invalid' : ''}
                        />
                        {errors.status && <small className="p-error">{errors.status}</small>}
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>Product Image</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                handleChange('productImage', file);
                            }}
                        />

                        {/* Preview */}
                        {form.productImage && (
                            <div className="mt-3">
                                <img src={URL.createObjectURL(form.productImage)} alt="Product Preview" style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ccc' }} />
                            </div>
                        )}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Barcode</label>
                        <InputText value={form.barcode} onChange={(e) => handleChange('barcode', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>
                            Effective From <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Calendar value={form.effectiveFrom} onChange={(e) => handleChange('effectiveFrom', e.value)} showIcon className={errors.effectiveFrom ? 'p-invalid' : ''} />
                        {errors.effectiveFrom && <small className="p-error">{errors.effectiveFrom}</small>}
                    </div>
                </div>

                <div className="mt-4 flex justify-end">
                    <Button label="Save" icon="pi pi-check" className="p-button-success" onClick={confirmSave} />
                </div>
            </div>
        </div>
    );
};

export default ProductMaster;
